import { create } from 'zustand';

import type {
  ConversationTurn,
  QueryResponse,
  ResponseFormat,
  SourceBadge,
} from '@/types/api';

export interface ChatMessage {
  turnId: string;
  role: 'user' | 'assistant';
  content: string;
  sparqlGenerated?: string | null;
  resultCount?: number;
  fallbackUsed?: boolean;
  confidence?: 'high' | 'medium' | 'low';
  sources?: SourceBadge[];
  clarificationChips?: string[];
  pending?: boolean;
  error?: string;
  responseFormat?: ResponseFormat;
}

interface ChatState {
  activeThreadId: string | null;
  isStreaming: boolean;
  responseFormat: ResponseFormat;
  messages: ChatMessage[];
  quotaRemaining: number | null;
  clarificationOpen: boolean;
}

interface ChatActions {
  setActiveThread: (threadId: string | null) => void;
  setResponseFormat: (format: ResponseFormat) => void;
  startSubmission: (userMessage: string) => string;
  finaliseSubmission: (pendingTurnId: string, response: QueryResponse) => void;
  failSubmission: (pendingTurnId: string, error: string) => void;
  hydrateFromHistory: (turns: ConversationTurn[]) => void;
  setQuotaRemaining: (remaining: number | null) => void;
  newChat: () => void;
}

type ChatStore = ChatState & ChatActions;

let pendingCounter = 0;

export const useChatStore = create<ChatStore>((set) => ({
  // ── state ────────────────────────────────────────────────────
  activeThreadId: null,
  isStreaming: false,
  responseFormat: 'text',
  messages: [],
  quotaRemaining: null,
  clarificationOpen: false,

  // ── actions ──────────────────────────────────────────────────
  setActiveThread: (threadId) =>
    set((state) => {
      // No-op when the URL is just catching up to the thread we're
      // already on (e.g. right after the post-submit navigate). Wiping
      // messages here would erase the freshly-rendered turn for a
      // beat before `hydrateFromHistory` re-builds them.
      if (state.activeThreadId === threadId) return state;
      return {
        activeThreadId: threadId,
        messages: [],
        clarificationOpen: false,
      };
    }),

  setResponseFormat: (format) => set({ responseFormat: format }),

  startSubmission: (userMessage) => {
    pendingCounter += 1;
    const pendingId = `pending-${Date.now()}-${pendingCounter}`;
    set((state) => ({
      isStreaming: true,
      messages: [
        ...state.messages,
        {
          turnId: `user-${pendingId}`,
          role: 'user',
          content: userMessage,
          responseFormat: state.responseFormat,
        },
        {
          turnId: pendingId,
          role: 'assistant',
          content: '',
          pending: true,
        },
      ],
    }));
    return pendingId;
  },

  finaliseSubmission: (pendingTurnId, response) =>
    set((state) => ({
      isStreaming: false,
      activeThreadId: response.thread_id,
      quotaRemaining: response.quota_remaining,
      clarificationOpen: Boolean(response.clarification_request),
      messages: state.messages.map((m) =>
        m.turnId === pendingTurnId
          ? {
              turnId: response.turn_id,
              role: 'assistant',
              content: response.response,
              sparqlGenerated: response.sparql_generated,
              resultCount: response.result_count,
              fallbackUsed: response.fallback_used,
              confidence: response.confidence,
              sources: response.sources,
              clarificationChips: response.clarification_chips,
              responseFormat: response.response_format,
            }
          : m,
      ),
    })),

  failSubmission: (pendingTurnId, error) =>
    set((state) => ({
      isStreaming: false,
      messages: state.messages.map((m) =>
        m.turnId === pendingTurnId
          ? { ...m, pending: false, error, content: '' }
          : m,
      ),
    })),

  hydrateFromHistory: (turns) =>
    set({
      messages: turns.map((t) => ({
        turnId: t.turn_id,
        role: t.role,
        content: t.content,
        sparqlGenerated: t.sparql_generated,
        resultCount: t.result_count ?? undefined,
        fallbackUsed: t.fallback_used,
      })),
    }),

  setQuotaRemaining: (remaining) => set({ quotaRemaining: remaining }),

  newChat: () =>
    set({
      activeThreadId: null,
      messages: [],
      clarificationOpen: false,
    }),
}));
